import { Box, Link, Paper, Stack, Typography } from "@mui/material";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import React, { useEffect } from "react";
import VoiceAgentSuccess from "./voice-agent-success";
import SchedulerSuccess from "./scheduler-success";
import { maskEmail } from "./signup-email";

const PAGE_SX = {
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  bgcolor: "#F5F7FA",
  p: { xs: 2, sm: 4 },
  boxSizing: "border-box",
};

const PAPER_SX = {
  width: "100%",
  maxWidth: 520,
  p: { xs: 4, sm: 6 },
  borderRadius: 4,
  textAlign: "center",
};

// Soft brand-blue halo behind the headline icon.
const ICON_HALO_SX = {
  width: 72,
  height: 72,
  borderRadius: "50%",
  bgcolor: "rgba(83, 166, 214, 0.12)",
  color: "#0994F2",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

function MinimalSuccessScreen({ email, onStartOver }) {
  const maskedEmail = maskEmail(email);

  const body = maskedEmail ? (
    <>
      We sent an email to{" "}
      <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
        {maskedEmail}
      </Box>
      . Check your inbox to finish setting up your account.
    </>
  ) : (
    "Check your inbox to finish setting up your account."
  );

  useEffect(() => {
    const previousTitle = document.title;
    document.title = "Check Your Email — Cinch";
    return () => {
      document.title = previousTitle;
    };
  }, []);

  return (
    <Box sx={PAGE_SX}>
      <Paper elevation={3} sx={PAPER_SX}>
        <Stack spacing={3} alignItems="center">
          <Box sx={ICON_HALO_SX}>
            <MarkEmailReadOutlinedIcon sx={{ fontSize: 36 }} />
          </Box>
          <Typography variant="h4" fontWeight={600}>
            Check your email
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 380 }}>
            {body}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ pt: 1 }}>
            Didn't get the email? Check your spam folder
            {onStartOver ? (
              <>
                , or{" "}
                <Link
                  component="button"
                  type="button"
                  onClick={onStartOver}
                  underline="hover"
                  sx={{
                    fontWeight: 600,
                    color: "#0994F2",
                    cursor: "pointer",
                    verticalAlign: "baseline",
                    font: "inherit",
                    p: 0,
                    border: 0,
                    background: "none",
                  }}
                >
                  try again
                </Link>
              </>
            ) : null}
            .
          </Typography>
        </Stack>
      </Paper>
    </Box>
  );
}

/**
 * Post-submit terminal state of the signup flow. The screen is the same
 * regardless of whether the email is new or already has an account — diverging
 * UX here would let anyone probe the public signup endpoint to enumerate Cinch
 * user emails. The backend handles the two cases server-side: new emails get the
 * "Set your password" email, existing emails get a "we noticed a signup attempt"
 * notice with sign-in + forgot-password links.
 *
 * AI Voice (CIN-6934) and Scheduler (CIN-6930) each get a full marketing
 * confirmation page; a missing or unrecognized feature_focus keeps the default
 * minimal "check your email" card.
 */
export default function SignupSuccessScreen({ email, onStartOver, featureFocus }) {
  if (featureFocus === "voice-agent") {
    return <VoiceAgentSuccess email={email} onStartOver={onStartOver} featureFocus={featureFocus} />;
  }
  if (featureFocus === "scheduler") {
    return <SchedulerSuccess email={email} onStartOver={onStartOver} featureFocus={featureFocus} />;
  }
  return <MinimalSuccessScreen email={email} onStartOver={onStartOver} />;
}
